import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Flip } from "gsap/Flip";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
} from "react";
import { flushSync } from "react-dom";
import { Button as AriaButton } from "react-aria-components";

import closeIcon from "@/assets/figma/chat-activity/close.svg";
import abnormalIcon from "@/assets/figma/chat-activity/abnormal.svg";
import deckBack from "@/assets/figma/chat-activity/deck-back.svg";
import deckMiddle from "@/assets/figma/chat-activity/deck-middle.svg";
import expandIcon from "@/assets/figma/chat-activity/expand.svg";
import manageSeparator from "@/assets/figma/chat-activity/separator.svg";
import collapseIcon from "@/assets/figma/component-bar/manage.svg";
import markAllReadIcon from "@/assets/figma/card-menu/notification-read-all.svg";
import { cn } from "@/shared/lib/cn";
import {
  MerryCardMenu,
  type MerryCardMenuAction,
  type MerryCardMenuCatalog,
} from "@/shared/ui/MerryCardMenu";
import { MerryChatThreadSummary } from "@/shared/ui/MerryChatThreadSummary";
import {
  getMerryChatHistoryDrawerSize,
  getMerryChatNotificationFigmaNodeId,
  type MerryChatActivityItem,
} from "@/shared/ui/merryChatActivityModel";

gsap.registerPlugin(Flip, useGSAP);

const notificationMotion = {
  duration: 0.38,
  ease: "power3.out",
} as const;

const managementMotion = {
  duration: 0.18,
  ease: "power2.out",
} as const;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getNotificationMotionTargets(root: HTMLDivElement) {
  return [
    root,
    ...root.querySelectorAll<HTMLElement>(
      "[data-chat-notification-layout-item]",
    ),
  ];
}

function NotificationGlassLayers() {
  return (
    <>
      <span aria-hidden="true" className="merry-chat-notification__glass" />
      <span aria-hidden="true" className="merry-chat-notification__stroke" />
    </>
  );
}

function NotificationControlButton({
  expanded,
  icon,
  label,
  onPress,
}: {
  expanded?: boolean;
  icon: string;
  label: string;
  onPress: () => void;
}) {
  return (
    <AriaButton
      aria-expanded={expanded}
      aria-label={label}
      className={({ isFocusVisible, isHovered, isPressed }) =>
        cn(
          "merry-chat-notification__control-button",
          isHovered && "merry-chat-notification__control-button--hovered",
          isPressed && "merry-chat-notification__control-button--pressed",
          isFocusVisible &&
            "merry-chat-notification__control-button--focus-visible",
        )
      }
      onPress={onPress}
      type="button"
    >
      <img alt="" aria-hidden="true" src={icon} />
    </AriaButton>
  );
}

function NotificationSeparator() {
  return (
    <span aria-hidden="true" className="merry-chat-notification__separator">
      <img alt="" src={manageSeparator} />
    </span>
  );
}

function NotificationManagement({
  isExpanded,
  isVisible,
  onCollapse,
  onDismiss,
  onExpand,
  onMarkAllRead,
}: {
  isExpanded: boolean;
  isVisible: boolean;
  onCollapse: () => void;
  onDismiss: () => void;
  onExpand: () => void;
  onMarkAllRead: () => void;
}) {
  return (
    <>
      {isExpanded ? (
        <div
          aria-hidden={!isVisible || undefined}
          className="merry-chat-notification__collapse-control"
          data-chat-notification-management="true"
          inert={!isVisible || undefined}
        >
          <NotificationGlassLayers />
          <NotificationControlButton
            expanded
            icon={collapseIcon}
            label="收起多条通知"
            onPress={onCollapse}
          />
        </div>
      ) : null}

      <div
        aria-hidden={!isVisible || undefined}
        className="merry-chat-notification__management"
        data-chat-notification-expanded={isExpanded ? "true" : "false"}
        data-chat-notification-management="true"
        inert={!isVisible || undefined}
      >
        <NotificationGlassLayers />
        <span className="merry-chat-notification__management-content">
          {!isExpanded ? (
            <>
              <NotificationControlButton
                expanded={false}
                icon={expandIcon}
                label="展开多条通知"
                onPress={onExpand}
              />
              <NotificationSeparator />
            </>
          ) : null}
          <NotificationControlButton
            icon={markAllReadIcon}
            label="全部已读"
            onPress={onMarkAllRead}
          />
          <NotificationSeparator />
          <NotificationControlButton
            icon={closeIcon}
            label="关闭通知"
            onPress={onDismiss}
          />
        </span>
      </div>
    </>
  );
}

export interface MerryChatNotificationStackProps {
  className?: string;
  defaultExpanded?: boolean;
  expanded?: boolean;
  items: ReadonlyArray<MerryChatActivityItem>;
  onDismiss: () => void;
  onExpandedChange?: (isExpanded: boolean) => void;
  onMarkAllRead: () => void;
  onOpenConversation?: (conversationId: string) => void;
}

export function MerryChatNotificationStack({
  className,
  defaultExpanded = false,
  expanded,
  items,
  onDismiss,
  onExpandedChange,
  onMarkAllRead,
  onOpenConversation,
}: MerryChatNotificationStackProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingLayoutRef = useRef<ReturnType<typeof Flip.getState> | null>(
    null,
  );
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const isExpanded = expanded ?? internalExpanded;
  const hasMultipleItems = items.length > 1;
  const isManagementVisible =
    hasMultipleItems && (isHovered || isFocusWithin);
  const mode = isExpanded
    ? "expanded"
    : hasMultipleItems
      ? "stacked"
      : "single";
  const figmaNodeId = getMerryChatNotificationFigmaNodeId({
    isExpanded,
    isManagementVisible,
    items,
  });

  const captureLayout = useCallback(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) {
      pendingLayoutRef.current = null;
      return;
    }

    const targets = getNotificationMotionTargets(root);
    Flip.killFlipsOf(targets, true);
    pendingLayoutRef.current = Flip.getState(targets, {
      props: "opacity,visibility",
    });
  }, []);

  const changeExpanded = useCallback(
    (nextExpanded: boolean) => {
      if (nextExpanded === isExpanded || !hasMultipleItems) return;

      captureLayout();
      if (expanded === undefined) {
        flushSync(() => setInternalExpanded(nextExpanded));
      }
      onExpandedChange?.(nextExpanded);
    }, [captureLayout, expanded, hasMultipleItems, isExpanded, onExpandedChange],
  );

  useGSAP(
    () => {
      const root = rootRef.current;
      const previousLayout = pendingLayoutRef.current;
      pendingLayoutRef.current = null;
      if (!root) return;

      const targets = getNotificationMotionTargets(root);
      if (!previousLayout || prefersReducedMotion()) {
        Flip.killFlipsOf(targets, true);
        gsap.set(targets, {
          clearProps: "height,opacity,transform,visibility,width,willChange",
        });
        root.dataset.chatNotificationAnimating = "false";
        return;
      }

      root.dataset.chatNotificationAnimating = "true";
      gsap.set(targets, { willChange: "height, opacity, transform" });

      const clearMotion = () => {
        root.dataset.chatNotificationAnimating = "false";
        gsap.set(targets, { clearProps: "willChange" });
      };

      Flip.from(previousLayout, {
        duration: notificationMotion.duration,
        ease: notificationMotion.ease,
        nested: true,
        prune: true,
        scale: false,
        onComplete: clearMotion,
        onInterrupt: clearMotion,
      });
    },
    { dependencies: [isExpanded], scope: rootRef },
  );

  useGSAP(
    () => {
      const root = rootRef.current;
      if (!root) return;

      const targets = Array.from(
        root.querySelectorAll<HTMLElement>(
          "[data-chat-notification-management]",
        ),
      );
      if (targets.length === 0) return;

      gsap.killTweensOf(targets);
      if (prefersReducedMotion()) {
        gsap.set(targets, {
          autoAlpha: isManagementVisible ? 1 : 0,
          clearProps: "transform",
        });
        return;
      }

      gsap.to(targets, {
        autoAlpha: isManagementVisible ? 1 : 0,
        duration: managementMotion.duration,
        ease: managementMotion.ease,
        overwrite: true,
        scale: isManagementVisible ? 1 : 0.985,
        transformOrigin: "top right",
      });
    },
    { dependencies: [isExpanded, isManagementVisible], scope: rootRef },
  );

  const handleBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsFocusWithin(false);
  };

  const dismiss = () => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) {
      onDismiss();
      return;
    }

    gsap.killTweensOf(root);
    root.dataset.chatNotificationAnimating = "true";
    gsap.to(root, {
      autoAlpha: 0,
      duration: 0.18,
      ease: "power2.in",
      overwrite: true,
      y: -8,
      onComplete: onDismiss,
      onInterrupt: () => {
        root.dataset.chatNotificationAnimating = "false";
      },
    });
  };

  if (items.length === 0) return null;

  // Figma 的“进行中”状态只保留左侧导航状态，不出现右上通知卡。
  if (items.length === 1 && items[0]?.status === "progress") return null;

  return (
    <section
      aria-label="聊天任务通知"
      className={cn("merry-chat-notification", className)}
      data-chat-notification-animating="false"
      data-chat-notification-mode={mode}
      data-figma-node-id={figmaNodeId ?? undefined}
      onBlurCapture={handleBlur}
      onFocusCapture={() => setIsFocusWithin(true)}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      ref={rootRef}
    >
      <div
        className="merry-chat-notification__list"
        data-chat-notification-layout-item="list"
      >
        {items.map((item, index) => (
          <AriaButton
            aria-label={`打开聊天：${item.title}`}
            className={({ isFocusVisible, isHovered: isCardHovered, isPressed }) =>
              cn(
                "merry-chat-notification__card",
                isCardHovered && "merry-chat-notification__card--hovered",
                isPressed && "merry-chat-notification__card--pressed",
                isFocusVisible &&
                  "merry-chat-notification__card--focus-visible",
              )
            }
            data-chat-notification-card-id={item.id}
            data-chat-notification-layout-item="card"
            key={item.id}
            onPress={() => onOpenConversation?.(item.id)}
            style={{ zIndex: items.length - index + 2 }}
            type="button"
          >
            <NotificationGlassLayers />
            <MerryChatThreadSummary
              className="merry-chat-notification__summary"
              iconSource={abnormalIcon}
              item={item}
            />
          </AriaButton>
        ))}
      </div>

      {hasMultipleItems ? (
        <>
          <span
            aria-hidden="true"
            className="merry-chat-notification__deck merry-chat-notification__deck--middle"
            data-chat-notification-layout-item="deck-middle"
          >
            <img alt="" src={deckMiddle} />
          </span>
          <span
            aria-hidden="true"
            className="merry-chat-notification__deck merry-chat-notification__deck--back"
            data-chat-notification-layout-item="deck-back"
          >
            <img alt="" src={deckBack} />
          </span>
        </>
      ) : null}

      {hasMultipleItems ? (
        <NotificationManagement
          isExpanded={isExpanded}
          isVisible={isManagementVisible}
          onCollapse={() => changeExpanded(false)}
          onDismiss={dismiss}
          onExpand={() => changeExpanded(true)}
          onMarkAllRead={onMarkAllRead}
        />
      ) : null}
    </section>
  );
}

export interface MerryChatHistoryDrawerProps {
  catalog: MerryCardMenuCatalog;
  className?: string;
  largeContentThreshold?: number;
  onAction?: (action: MerryCardMenuAction) => void;
}

export function MerryChatHistoryDrawer({
  catalog,
  className,
  largeContentThreshold = 7,
  onAction,
}: MerryChatHistoryDrawerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const itemCount = catalog.chatHistory.sections.reduce(
    (count, section) => count + section.items.length,
    0,
  );
  const drawerSize = getMerryChatHistoryDrawerSize(
    itemCount,
    largeContentThreshold,
  );
  const isLargeContent = drawerSize === "viewport";

  useLayoutEffect(() => {
    if (isLargeContent) return;

    const root = rootRef.current;
    if (!root) return;

    const updateAvailableHeight = () => {
      const top = root.getBoundingClientRect().top;
      const availableHeight = Math.max(0, window.innerHeight - top - 24);
      root.style.setProperty(
        "--merry-chat-history-height",
        `${Math.min(656, availableHeight)}px`,
      );
    };

    updateAvailableHeight();
    window.addEventListener("resize", updateAvailableHeight);
    return () => window.removeEventListener("resize", updateAvailableHeight);
  }, [isLargeContent]);

  return (
    <div
      aria-label="聊天历史列表"
      className={cn("merry-chat-history-drawer", className)}
      data-chat-history-size={drawerSize}
      data-figma-node-id={isLargeContent ? "567:123787" : "567:122750"}
      ref={rootRef}
      role="region"
    >
      <MerryCardMenu
        animateEntrance={false}
        aria-label="聊天历史"
        catalog={catalog}
        className="merry-chat-history-drawer__menu"
        data-testid="merry-chat-history-menu"
        onAction={(action) => onAction?.(action)}
        view="chat-history"
      />
    </div>
  );
}
